import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import Container from '@/components/generals/container'
import SectionHeading from '@/components/generals/section-heading'
import { quoteSteps } from '@/constants/content'
import { quoteHref } from '@/constants/site'

interface QuoteProcessProps {
    id?: string
    tone?: 'graphite' | 'black'
    showButton?: boolean
}

const QuoteProcess: FC<QuoteProcessProps> = ({ id = 'proceso', tone = 'graphite', showButton = true }): ReactNode => {
    return (
        <section
            id={id}
            aria-labelledby={`${id}-titulo`}
            className={cn('surface-grain relative py-24 md:py-32', tone === 'graphite' ? 'bg-graphite' : 'bg-black')}
        >
            <Container>
                <SectionHeading
                    id={`${id}-titulo`}
                    align='center'
                    eyebrow='Proceso de cotización'
                    title='Tu reconocimiento en cuatro pasos.'
                    description='Un proceso claro para pasar de la idea a una propuesta concreta, sin compromisos hasta que confirmes los detalles.'
                />

                <ol className='mt-16 grid gap-10 md:grid-cols-2 lg:grid-cols-4 lg:gap-8'>
                    {quoteSteps.map(step => (
                        <li key={step.number} className='reveal relative flex flex-col gap-4 border-t border-brass/40 pt-8'>
                            <span aria-hidden='true' className='absolute -top-[5px] left-0 size-2.5 rotate-45 bg-brass' />

                            <span className='font-heading text-5xl leading-none font-medium text-brass'>
                                {step.number}
                            </span>

                            <h3 className='font-heading text-2xl leading-tight font-medium text-foreground'>
                                {step.title}
                            </h3>

                            <p className='text-sm leading-relaxed text-muted-foreground'>
                                {step.description}
                            </p>
                        </li>
                    ))}
                </ol>

                {showButton && (
                    <div className='mt-16 flex justify-center'>
                        <Link href={quoteHref} className={buttonVariants({ size: 'lg' })}>
                            Iniciar cotización
                            <ArrowRight aria-hidden='true' />
                        </Link>
                    </div>
                )}
            </Container>
        </section>
    )
}

export default QuoteProcess
